import { create } from 'zustand';
import type { WorkareaTab } from '@/types';

const MAX_ENTRIES = 20;

/**
 * Session-scoped ring buffer of the last ~20 tabs the user closed. Powers
 * `@recent` search and future "Reopen Closed Tab" commands. Not persisted —
 * a reload clears it (matches browser behavior: "reopen recently closed"
 * doesn't survive a restart).
 *
 * We store the full tab shape because reopening needs every field (where,
 * orderBy, columnConfig, savedQueryId, etc.). Titles/subtitles are derived
 * on demand in the search provider.
 */
interface RecentTabStore {
  entries: Array<{ tab: WorkareaTab; closedAt: number }>;
  pushClosed: (tab: WorkareaTab) => void;
  clear: () => void;
}

export const useRecentTabStore = create<RecentTabStore>((set) => ({
  entries: [],
  pushClosed: (tab) => set((s) => {
    // Drop any previous entry for the same tab id so the buffer never
    // shows the same tab twice — a user closing and reopening repeatedly
    // still only produces one row.
    const filtered = s.entries.filter((e) => e.tab.id !== tab.id);
    const next = [{ tab, closedAt: Date.now() }, ...filtered].slice(0, MAX_ENTRIES);
    return { entries: next };
  }),
  clear: () => set({ entries: [] }),
}));
